import type React from "react";
import { generateId } from "@/lib/utils";

// 인라인 서식 타입
export type InlineFormat = "bold" | "italic" | "underline" | "strikethrough" | "code" | "link";

// 인라인 서식 설정
export const inlineFormatConfig: Record<
  InlineFormat,
  {
    label: string;
    icon: string;
    shortcut: string;
    tag: string;
    className?: string;
  }
> = {
  bold: {
    label: "굵게",
    icon: "B",
    shortcut: "Ctrl+B",
    tag: "strong",
  },
  italic: {
    label: "기울임",
    icon: "I",
    shortcut: "Ctrl+I",
    tag: "em",
  },
  underline: {
    label: "밑줄",
    icon: "U",
    shortcut: "Ctrl+U",
    tag: "u",
  },
  strikethrough: {
    label: "취소선",
    icon: "S",
    shortcut: "Ctrl+Shift+S",
    tag: "s",
  },
  code: {
    label: "인라인 코드",
    icon: "</>",
    shortcut: "Ctrl+E",
    tag: "code",
    className: "inline-code",
  },
  link: {
    label: "링크",
    icon: "🔗",
    shortcut: "Ctrl+K",
    tag: "a",
  },
};

// 블록 타입 정의
export type BlockType =
  | "paragraph"
  | "heading1"
  | "heading2"
  | "heading3"
  | "bulletList"
  | "numberedList"
  | "todoList"
  | "quote"
  | "divider"
  | "code"
  | "image"
  | "video"
  | "audio"
  | "gallery"
  | "table"
  | "database_inline"
  | "database_full";

// 테이블 셀 인터페이스
export interface TableCell {
  content: string;
  isHeader?: boolean;
}

// 테이블 데이터 인터페이스
export interface TableData {
  rows: TableCell[][];
  hasHeaderRow?: boolean;
  hasHeaderColumn?: boolean;
}

// 갤러리 이미지 인터페이스
export interface GalleryImage {
  id: string;
  url: string;
  caption?: string;
}

// 비디오 소스 타입
export type VideoSource = "youtube" | "vimeo" | "file";

// 이미지 정렬 타입
export type ImageAlign = "left" | "center" | "right";

// 블록 색상 타입
export type BlockColor =
  | "default"
  | "gray"
  | "brown"
  | "orange"
  | "yellow"
  | "green"
  | "blue"
  | "purple"
  | "pink"
  | "red";

export interface Block {
  id: string;
  type: BlockType;
  content: string;
  checked?: boolean; // todoList용
  children?: Block[]; // 중첩 블록용 (나중에)
  color?: BlockColor; // 텍스트 색상
  backgroundColor?: BlockColor; // 배경 색상
  // 이미지 블록용
  imageUrl?: string;
  imageWidth?: number; // 표시 너비 (px)
  imageAlign?: ImageAlign;
  // 비디오 블록용
  videoUrl?: string;
  videoSource?: VideoSource;
  // 오디오 블록용
  audioUrl?: string;
  // 테이블 블록용
  tableData?: TableData;
  // 갤러리 블록용
  galleryImages?: GalleryImage[];
  galleryColumns?: 2 | 3 | 4;
  // 데이터베이스 블록용
  databaseId?: string; // 연결된 데이터베이스 ID
}

// 색상 설정
export const blockColorConfig: Record<BlockColor, { label: string; textClass: string; bgClass: string }> = {
  default: { label: "기본", textClass: "text-neutral-900", bgClass: "bg-transparent" },
  gray: { label: "회색", textClass: "text-neutral-500", bgClass: "bg-neutral-100" },
  brown: { label: "갈색", textClass: "text-amber-700", bgClass: "bg-amber-50" },
  orange: { label: "주황", textClass: "text-orange-600", bgClass: "bg-orange-50" },
  yellow: { label: "노랑", textClass: "text-yellow-600", bgClass: "bg-yellow-50" },
  green: { label: "초록", textClass: "text-green-600", bgClass: "bg-green-50" },
  blue: { label: "파랑", textClass: "text-blue-600", bgClass: "bg-blue-50" },
  purple: { label: "보라", textClass: "text-purple-600", bgClass: "bg-purple-50" },
  pink: { label: "분홍", textClass: "text-pink-600", bgClass: "bg-pink-50" },
  red: { label: "빨강", textClass: "text-red-600", bgClass: "bg-red-50" },
};

export interface MediaBlockProps {
  block: Block;
  isActive: boolean;
  readOnly: boolean;
  isDragging: boolean;
  isDropTarget: boolean;
  onFocus: () => void;
  onBlur: () => void;
  onChange: (updates: Partial<Block>) => void;
  onDelete: () => void;
  onAddBlockAfter: () => void;
  onTransform: (type: BlockType) => void;
  onColorChange: (color: BlockColor | undefined, backgroundColor: BlockColor | undefined) => void;
  onDuplicate: () => void;
  onCopyLink: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}

export interface PageContent {
  blocks: Block[];
  version: number; // 충돌 감지용
}

// 블록 타입별 메타데이터
export const blockTypeConfig: Record<
  BlockType,
  {
    label: string;
    icon: string;
    placeholder: string;
    shortcut?: string;
  }
> = {
  paragraph: {
    label: "텍스트",
    icon: "📝",
    placeholder: "내용을 입력하세요...",
  },
  heading1: {
    label: "제목 1",
    icon: "H1",
    placeholder: "제목 1",
    shortcut: "# ",
  },
  heading2: {
    label: "제목 2",
    icon: "H2",
    placeholder: "제목 2",
    shortcut: "## ",
  },
  heading3: {
    label: "제목 3",
    icon: "H3",
    placeholder: "제목 3",
    shortcut: "### ",
  },
  bulletList: {
    label: "글머리 기호 목록",
    icon: "•",
    placeholder: "목록 항목",
    shortcut: "- ",
  },
  numberedList: {
    label: "번호 매기기 목록",
    icon: "1.",
    placeholder: "목록 항목",
    shortcut: "1. ",
  },
  todoList: {
    label: "할 일 목록",
    icon: "☐",
    placeholder: "할 일",
    shortcut: "[] ",
  },
  quote: {
    label: "인용",
    icon: "❝",
    placeholder: "인용문",
    shortcut: "> ",
  },
  divider: {
    label: "구분선",
    icon: "—",
    placeholder: "",
    shortcut: "---",
  },
  code: {
    label: "코드",
    icon: "</>",
    placeholder: "코드를 입력하세요...",
    shortcut: "```",
  },
  image: {
    label: "이미지",
    icon: "🖼️",
    placeholder: "캡션을 입력하세요...",
  },
  video: {
    label: "비디오",
    icon: "🎬",
    placeholder: "캡션을 입력하세요...",
  },
  audio: {
    label: "오디오",
    icon: "🎵",
    placeholder: "캡션을 입력하세요...",
  },
  gallery: {
    label: "갤러리",
    icon: "🖼️",
    placeholder: "",
  },
  table: {
    label: "테이블",
    icon: "📊",
    placeholder: "",
  },
  database_inline: {
    label: "인라인 데이터베이스",
    icon: "📋",
    placeholder: "",
  },
  database_full: {
    label: "전체 페이지 데이터베이스",
    icon: "🗃️",
    placeholder: "",
  },
};

// 기본 테이블 데이터 생성
export function createDefaultTableData(rows = 3, cols = 3): TableData {
  const tableRows: TableCell[][] = [];
  for (let i = 0; i < rows; i++) {
    const row: TableCell[] = [];
    for (let j = 0; j < cols; j++) {
      row.push({ content: "", isHeader: i === 0 });
    }
    tableRows.push(row);
  }
  return { rows: tableRows, hasHeaderRow: true };
}

// 새 블록 생성 헬퍼
export function createBlock(type: BlockType = "paragraph", content = ""): Block {
  return {
    id: generateId(),
    type,
    content,
    ...(type === "todoList" ? { checked: false } : {}),
    ...(type === "table" ? { tableData: createDefaultTableData() } : {}),
    ...(type === "gallery" ? { galleryImages: [], galleryColumns: 3 } : {}),
  };
}

// 빈 페이지 콘텐츠 생성
export function createEmptyPageContent(): PageContent {
  return {
    blocks: [createBlock("paragraph")],
    version: 1,
  };
}
